import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivationStart, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { oneTrustModule } from '../../assets/scripts/one-trust-lang-change';
import { environment } from '../../environments/environment';
import { DestinationType } from '../comun/recipient-services/DestinationType';
import { Change } from '../comun/recipient-services/change';
import { Expedition } from '../comun/recipient-services/expedition';
import { ExpeditionModification } from '../comun/recipient-services/expedition-modification';
import { ChangeType } from '../enums/change-type.enum';
import { ExpeditionsService } from './expeditions.service';
import { LanguageService } from './language.service';

export const CategoriasGoogleAnalytics = {
  ConsultaExpedicion: 'Consulta Expedición',
  ExpeditionModification: 'Modificación Expedición',
  Otros: 'Otros'
};
export const paths = {
  home: {
    es: '',
    en: 'Home',
    pt: 'Iniciação'
  },
  homeBusiness: {
    es: 'seguimiento-envio-empresas',
    en: 'business',
    pt: 'empresas'
  },
  modificarEnvio: {
    es: 'ModificarEnvio',
    en: 'ModifyMyShipment',
    pt: 'ModificarMeuEnvio'
  },
  noData: {
    es: 'SinDatos',
    en: 'NoInformation',
    pt: 'SemDetalhes'
  },
  withoutModification: {
    es: 'SinModificacion',
    en: 'NoModifications',
    pt: 'SemAlteraçoes'
  },
  changesMade: {
    es: 'CambiosRealizados',
    en: 'ChangesDone',
    pt: 'AlteraçõesEfetuadas'
  },
  entregaVecino: {
    es: 'EntregaEnVecino',
    en: 'DeliveryToNeighbour',
    pt: 'EntregaVizinho'
  },
  entregaServicePoint: {
    es: 'EntregaEnServicePoint',
    en: 'DeliveryInServicePoint',
    pt: 'EntregaNumServicePoint'
  },
  changeDay: {
    es: 'ModificarDiaEntrega',
    en: 'ChangeDeliveryDate',
    pt: 'AlterarDiaEntrega'
  },
  alternativeDirectionSolution: {
    es: 'EntregaDireccionAlternativa',
    en: 'DeliveryAlternativeDirection',
    pt: 'direção'
  },
  dateChangeSolution: {
    es: 'SolucionDia',
    en: 'DaySolution',
    pt: 'SoluçaoDia'
  },
  solutionPickUpDHLFacilities: {
    es: 'SolucionRecogerInstalacionesDHL',
    en: 'PickUpDHLFacilities',
    pt: 'RecolherInstalaçoesDHL'
  },
  senderRecipientSolution: {
    es: 'RemitenteDestinatario',
    en: 'SenderRecipient',
    pt: 'RemetenteDestinatario'
  },
  newAddress: {
    es: 'NuevaDireccion',
    en: 'NewDirection',
    pt: 'NovaDireçao'
  },
  proofDeliveryTerms: {
    es: 'TerminosComprobanteEntrega',
    en: 'ProofDeliveryTerms',
    pt: 'TermosComprovativoEntrega'
  },
  offline: {
    es: 'AplicacionOffline',
    en: 'ApplicationnOffline',
    pt: 'AplicaçãoOffline'
  }
};

@Injectable()
export class SesionService {
  private gaConfigured = false;
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly expedicionesService: ExpeditionsService,
    private langService: LanguageService,

    @Inject(DOCUMENT) private document: Document
  ) {
    this.expedition$ = new BehaviorSubject<Expedition>(null);
    this.subscribeLangChange();
    this.router.events.pipe(
      filter(event => event instanceof ActivationStart)
    ).subscribe((event: ActivationStart) => {
      if ((event.snapshot.firstChild && event.snapshot.firstChild.data.idioma) ||
        event.snapshot.data.idioma) {
        const idioma = event.snapshot.firstChild && event.snapshot.firstChild.data.idioma
          ? event.snapshot.firstChild.data.idioma
          : event.snapshot.data.idioma;
        if (idioma !== this.idioma$.value && idioma !== undefined) {
          this.idioma$.next(idioma);
          this.langService.setLanguage(idioma);
          this.rutaModificarEnvio = '/' + paths.modificarEnvio[idioma];
          this.routeNoData = '/' + paths.noData[idioma];
          this.routeWithoutModification = '/' + paths.withoutModification[idioma];
          this.rutaCambiosRealizados = '/' + paths.changesMade[idioma];
        }
      }
    });
    router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((navigationEvent: NavigationEnd) => {
        this.checkIfAnalyticsLoaded().then(() => {
          this.sendPageView(navigationEvent.urlAfterRedirects);
        });
      });
  }

  public get modification(): ExpeditionModification {
    return this._modificacion;
  }

  get zipCodeMask(): string {
    if (this.destinationType === DestinationType.Portugal) {
      return '0000-000';
    } else if (this.destinationType === DestinationType.España) {
      return '00000';
    } else {
      return null;
    }
  }

  get destinationType(): DestinationType {
    const expedition = this.expedition$.getValue();
    return expedition && expedition.DestinationType;
  }

  tipoCambioActivo = ChangeType.Ninguno;
  indiceFichaSeleccionadaCabecera$ = new BehaviorSubject<number>(environment.indiceFichaSeleccionadaCabecera);
  idioma$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  tablaRutas: Map<string, any>;

  rutaModificarEnvio = '';
  routeNoData = '';
  routeWithoutModification = '';
  rutaCambiosRealizados = '';
  urlBussines: string;

  expedition$: BehaviorSubject<Expedition>;
  numeroExpedicionBuscado: string;
  numeroExpedicionTLGBuscado: string;

  changesMade = ChangeType.Ninguno;
  changes = new Array<Change>();

  private _modificacion: ExpeditionModification;

  tiposCambioConfirmados: any = {};
  changesAvailable: Array<Change>;

  private urlsSinIndicadorCarga = new Array<string>();
  fechaFinMantenimiento?: Date;

  subscribeLangChange() {
    this.idioma$.subscribe((lang) => {

      if (this.document.documentElement.lang !== lang) {
        this.document.documentElement.lang = lang;
      }
      if (typeof oneTrustModule.changeCookiesLanguage !== 'undefined') {

        oneTrustModule.changeCookiesLanguage(lang)
      }
    })
  }

  rutaIdioma(idioma: string): BehaviorSubject<Array<any>> {
    const route$ = new BehaviorSubject<Array<any>>([]);
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (!this.tablaRutas) {
        this.tablaRutas = new Map<string, string>();
        for (const route in paths) {
          const pathsRuta = paths[route];
          for (const idioma in pathsRuta) {
            this.tablaRutas.set(pathsRuta[idioma], pathsRuta);
          }
        }
      }

      const valoresRuta = [];


      let route = this.route.snapshot;
      do {
        route = route.children[0];
        route.url.forEach(url => {
          if (this.tablaRutas.has(url.path)) {
            valoresRuta.push(this.tablaRutas.get(url.path)[idioma]);
          } else {
            valoresRuta.push(url.path);
          }
        });
      } while (route.children && route.children.length > 0);

      if (valoresRuta.length == 0) {
        valoresRuta.push(this.tablaRutas.get('')[idioma]);
      }

      route$.next(valoresRuta);
    });

    return route$;
  }

  async searchExpedition(expeditionNumber: string, zipCode?: string) {
    this._modificacion = null;
    this.changes = new Array<Change>();
    this.numeroExpedicionBuscado = expeditionNumber;
    const expedition = await lastValueFrom(this.expedicionesService.search(expeditionNumber, zipCode));
    this.tipoCambioActivo = ChangeType.Ninguno;
    this.expedition$.next(expedition);
    if (!expedition) {
      this.router.navigate([this.routeNoData]);
    } else {
      this.productType = expedition.ProductType;
      this.incidence = expedition.LastIncidence;
      this.numeroExpedicionBuscado = expedition.ExpeditionNumber;
      const datosRuta = [this.rutaModificarEnvio, expedition.ExpeditionNumber];
      if (zipCode) {
        datosRuta.push(zipCode);
        expedition.ZipCode = zipCode;
      }
      this.router.navigate(datosRuta, { queryParamsHandling: 'merge' });
    }
  }

  /** Devuelve los bultos */
  async searchExpeditionByReference(expeditionNumber: string): Promise<any[]> {
    this._modificacion = null;
    this.changes = new Array<Change>();
    this.numeroExpedicionBuscado = expeditionNumber;
    return await lastValueFrom(this.expedicionesService.searchByReference(expeditionNumber));
  }

  async modify(modification: ExpeditionModification): Promise<string> {
    const result = await this.expedicionesService.modify(modification).toPromise();
    this.expedition$.getValue().Tracking = result.Tracking;
    this._modificacion = modification;
    modification.Changes.forEach(change => {
      const nombreTipo = (<Map<number, string>>(<any>ChangeType).nombres).get(change.type);
      this.enviarEventoGoogleAnalitics(CategoriasGoogleAnalytics.ExpeditionModification, 'Proceso Completado', nombreTipo);
    });

    this.tiposCambioConfirmados = {};
    this.router.navigate([this.rutaCambiosRealizados]);
    return result.Message;
  }

  gaConfirmacion(type: ChangeType) {
    if (!this.tiposCambioConfirmados[type]) {
      const nombreTipo = (<Map<number, string>>(<any>ChangeType).nombres).get(type);
      this.tiposCambioConfirmados[type] = true;
      this.enviarEventoGoogleAnalitics(CategoriasGoogleAnalytics.ExpeditionModification, 'Confirmar Change', nombreTipo);
    }
  }

  registrarURLSinIndicadorCarga(url: string) {
    if (this.urlsSinIndicadorCarga.indexOf(url) == -1) {
      this.urlsSinIndicadorCarga.push(url);
    }
  }

  evitarIndicadorCarga(url: string): boolean {
    return this.urlsSinIndicadorCarga.indexOf(url) != -1;
  }

  app: string
  productType: string;
  incidence: string

  enviarEventoGoogleAnalitics(categoria: string, accion: string, etiqueta?: string) {
    this.checkIfAnalyticsLoaded().then(() => {
      gtag('event', accion, {
        event_category: categoria,
        event_label: etiqueta,
        app: this.app,
        productType: this.productType,
        incidence: this.incidence
      });
    })
  }

  public checkIfAnalyticsLoaded(): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      let timeStart = Date.now();
      const TIMEOUT = 300000;

      let _isLoaded = () => {
        if (Date.now() - timeStart > TIMEOUT) {
          reject('Timeout. Google analytics not injected!');
          return;
        }
        if ((window as any).gtag) {
          if (!this.gaConfigured) {
            this.gaConfigured = true
            gtag('config', environment.googleAnalyticsId, {
              custom_map: {
                dimension1: 'app',
                dimension2: 'productType',
                dimension3: 'incidence',
              }
            });
          }
          resolve();
          return;
        } else {
          setTimeout(_isLoaded, 500);
        }
      };

      _isLoaded();
    });
  }

  private sendPageView(url: string) {
    let pathGA = this.getPathWithoutParameters(this.route);
    if (pathGA?.length == 0) {
      pathGA = '/';
    }

    if (pathGA?.length > 0) {
      gtag('event', 'page_hit', {
        page_hit: pathGA,
        app: this.app,
        productType: this.productType,
        incidence: this.incidence
      });
    }
  }

  private getPathWithoutParameters(route: ActivatedRoute): string {
    let result = route.snapshot.children[0];
    let path = '';
    while (result.children.length > 0) {
      if (Object.getOwnPropertyNames(result.params).length === 0) {
        path = path + '/' + (result.url.length > 0 ? result.url[0].path : '');
      }

      result = result.children[0];
    }
    return path;
  }
}
